import { Timeline } from '../models/timeline.model';
import { TimelineSummary } from '../models/timeline-summary.model';
import { TimelineEvent } from '../models/timeline-event.model';
import { EngagementProcessStatus } from '../models/enums/engagement-process-status.enum';
import { TimelineState } from '../models/enums/timeline-state.enum';
import { TimelineKind } from '../models/enums/timeline-kind.enum';
import { TimelineEventStatus } from '../models/enums/timeline-event-status.enum';
import { TimelineActionType } from '../models/timeline-action-type.model';
import { EngagementProcess } from '../models/engagement-process.model';
import { EngagementBuyer } from '../models/engagement-buyer.model';
import { BuyerCatchUpState } from '../models/enums/buyer-catch-up-state.enum';
import { MarketplaceOpportunityStatus } from '../../opportunity/models/opportunity-status';
import { CatchUpPurpose } from '../models/enums/catch-up-purpose.enum';

// [#1298] After a resume the engagement is Current again, but its timeline stays frozen where it stopped while the
// resume's catch-up replays the engagement for the buyers who had left; it continues once the catch-up reaches it.
export const isWaitingForTheCatchUp = (timeline?: Timeline): boolean =>
    timeline?.engagementProcess?.status === EngagementProcessStatus.Current &&
    timeline?.state === TimelineState.SUSPENDED;

// [#1300] The resume's catch-up: the newest active catch-up master following the main, as the API picks it.
// [#1314] A Late Buyer's own catch-up follows the main too and is never the resume's.
export const resumeCatchUpOf = (timelines: TimelineSummary[]): TimelineSummary | undefined => {
    const main = timelines.find((timeline) => timeline.kind === TimelineKind.MAIN);
    return timelines
        .filter(
            (timeline) =>
                timeline.kind === TimelineKind.CATCH_UP_MASTER &&
                timeline.state === TimelineState.ACTIVE &&
                timeline.masterTimelineId === main?.timelineId &&
                timeline.catchUpPurpose !== CatchUpPurpose.LATE_BUYER
        )
        .sort((first, second) => second.timelineId - first.timelineId)[0];
};

const openStepOf = (catchUp: Timeline | undefined, actionType: TimelineActionType): TimelineEvent | undefined =>
    catchUp?.events?.find(
        (event) =>
            event.linkedAction?.actionType === actionType &&
            (event.status === TimelineEventStatus.InProgress || event.status === TimelineEventStatus.Late)
    );

// [#1300] The catch-up's teaser approval while it is open: the Seller reviews the confirmed teaser again on it, and
// confirms it once for the whole catch-up.
export const openTeaserApprovalOf = (catchUp?: Timeline): TimelineEvent | undefined =>
    openStepOf(catchUp, TimelineActionType.SellerTeaserApproval);

// [#1308] The catch-up's teaser posting while it is open: the Admin posts the SFAS teaser on it, not on the waiting main.
export const openTeaserPostingOf = (catchUp?: Timeline): TimelineEvent | undefined =>
    openStepOf(catchUp, TimelineActionType.VetValueBuyerSelectionAndTeaserPosting);

// [#1299] What every catch-up asks of the Seller again: the teaser, the DFM and PIP approvals, the data requests and the
// buyer selections. Every other Seller step completes on the catch-up by itself.
const STEPS_THE_SELLER_DOES_AGAIN = [
    TimelineActionType.SellerTeaserApproval,
    TimelineActionType.SellerEngagementDocument,
    TimelineActionType.SellerDataAndDocumentRequest,
    TimelineActionType.SellerBuyerSelectionIdNda,
    TimelineActionType.SellerCustomSelectDataNdaTerms,
    TimelineActionType.SellerBuyerSelectionDetailedFinancialModel,
];

// [#1299] The resume's catch-up opened in the drill-down: the Seller acts on its open steps there.
export const isTheResumeCatchUp = (timeline?: Timeline): boolean =>
    timeline?.kind === TimelineKind.CATCH_UP_MASTER && timeline?.state === TimelineState.ACTIVE;

// [#1299] The catch-up's steps open now that wait for the Seller.
export const stepsWaitingForTheSellerOn = (catchUp?: Timeline): TimelineEvent[] =>
    catchUp?.events?.filter(
        (event) =>
            STEPS_THE_SELLER_DOES_AGAIN.includes(event.linkedAction?.actionType) &&
            (event.status === TimelineEventStatus.InProgress || event.status === TimelineEventStatus.Late)
    ) ?? [];

// [#1299 F6] The documents the catch-up waits for the Seller to approve again - its open DFM and PIP approvals. They
// were approved before the break and read as done, so their Approve button is offered for the catch-up's sake.
export const documentsToApproveAgainOn = (catchUp?: Timeline): string[] =>
    stepsWaitingForTheSellerOn(catchUp)
        .filter((step) => step.linkedAction?.actionType === TimelineActionType.SellerEngagementDocument)
        .map((step) => step.linkedAction.referenceItemId)
        .filter((documentId) => !!documentId);

// [#1299] The catch-up's process as its riders see it - the buyers following it, as the Seller's own read tells them
// apart; a buyer still frozen is neither offered nor decided on the catch-up.
// [#1314] Several catch-ups run at once, one per Late Buyer, so the riders of this one are named by the engagement's
// summaries; the Catching up state alone would offer every catch-up's riders to a selection on any of them.
export const withOnlyTheRiders = (
    catchUpProcess: EngagementProcess,
    buyersOnTheSellersTimeline: EngagementBuyer[],
    ridersOfThisCatchUp: number[] = []
): EngagementProcess => {
    const riderIds = new Set(
        ridersOfThisCatchUp.length
            ? ridersOfThisCatchUp
            : buyersOnTheSellersTimeline
                  .filter((buyer) => buyer.catchUpState === BuyerCatchUpState.CATCHING_UP)
                  .map((buyer) => buyer.buyerId)
    );
    return { ...catchUpProcess, buyers: (catchUpProcess.buyers ?? []).filter((buyer) => riderIds.has(buyer.buyerId)) };
};

// [#1315 F6] The Seller's own windows on the main offer and count only the buyers on it: one whose timeline follows a
// catch-up - riding it, or frozen on it - is selected there, and the API refuses them on the main. Named by the
// engagement's summaries, as the API names them (a buyer timeline whose master is not the main). The store holds one
// engagement's summaries at a time, so they count only when they carry this engagement's main; without it nobody can be
// told apart, and the list stays as it is.
export const withoutTheRiders = (
    process: EngagementProcess | undefined,
    summaries: TimelineSummary[],
    mainTimelineId: number
): EngagementProcess | undefined => {
    const main = summaries.find(
        (summary) => summary.kind === TimelineKind.MAIN && summary.timelineId === mainTimelineId
    );
    if (!process || !main) {
        return process;
    }
    const riderIds = new Set(
        summaries
            .filter(
                (summary) =>
                    summary.kind === TimelineKind.BUYER &&
                    summary.buyerId !== undefined &&
                    summary.masterTimelineId !== undefined &&
                    summary.masterTimelineId !== main.timelineId
            )
            .map((summary) => summary.buyerId)
    );
    return { ...process, buyers: (process.buyers ?? []).filter((buyer) => !riderIds.has(buyer.buyerId)) };
};

// [#1299 F3] One round of a buyer selection step: the process that records who it was sent to, and the step's action.
export interface SelectionRound {
    process?: EngagementProcess;
    actionId?: number;
}

// [#1299 F3] The Seller's own step a catch-up step runs again, by definition. A batch sent from the catch-up is another
// round of it, as the server prices and invoices it.
export const sellersOwnRoundOf = (catchUpStep?: TimelineEvent, sellersTimeline?: Timeline): SelectionRound | undefined => {
    const sellersStep = sellersTimeline?.events?.find((event) => event.definitionId === catchUpStep?.definitionId);
    return sellersStep ? { process: sellersTimeline.engagementProcess, actionId: sellersStep.linkedAction?.id } : undefined;
};

// [#1299 F3] The buyers a selection step has already been sent to, each once across its rounds: the next batch's
// "first X as one" bundle is priced after them. [#1309] Marketplace buyer ids, as the selection sends them.
export const buyersAlreadySent = (rounds: SelectionRound[], statuses: MarketplaceOpportunityStatus[]): number[] =>
    Array.from(
        new Set(
            rounds
                .filter((round) => !!round)
                .flatMap(({ process, actionId }) =>
                    (process?.buyers ?? [])
                        .filter((buyer) =>
                            buyer.actionState?.some(
                                (state) => state.actionId === actionId && statuses.includes(state.buyerStatus)
                            )
                        )
                        .map((buyer) => buyer.buyerId)
                )
        )
    );
